import { AuditLogRecordType, UserType, Workload } from './management/schema.interface';

import { Reputation } from './reputation.interface';

export type Severity = 'info' | 'warning' | 'danger' | 'critical';

export const WorkloadGuard: { [key in Workload]: boolean } = {
    Aip: true,
    AeD: true,
    AirInvestigation: true,
    ApplicationAudit: true,
    AttackSim: true,
    AzureActiveDirectory: true,
    Compliance: true,
    CRM: true,
    Dynamics365: true,
    Endpoint: true,
    Exchange: true,
    Kaizala: true,
    MicrosoftFlow: true,
    MicrosoftForms: true,
    MicrosoftStream: true,
    MicrosoftTeams: true,
    MyAnalytics: true,
    OneDrive: true,
    PowerApps: true,
    PowerBI: true,
    PrivacyInsights: true,
    PublicEndpoint: true,
    Quarantine: true,
    SecurityComplianceCenter: true,
    SharePoint: true,
    SkypeForBusiness: true,
    ThreatIntelligence: true,
    WorkplaceAnalytics: true,
    Yammer: true,
};

export type MicrosoftModules = {
    [key in Workload]: Partial<{
        [key in AuditLogRecordType]: {
            [key: string]: EventMeta<any>;
        };
    }>;
};

export abstract class Predicates {
    // isAllowed predicates check EventRules
    protected abstract isAllowedASN(event: MicrosoftEvent): boolean;
    protected abstract isAllowedCountry(event: MicrosoftEvent): boolean;
    protected abstract isAllowedRegion(event: MicrosoftEvent): boolean;
    protected abstract isAllowedIP(event: MicrosoftEvent): boolean;
    // all other predicates are checked if/when defined as part of event classification
    protected abstract isUserMemberOfOrganisation(event: MicrosoftEvent): boolean;
    protected abstract isFraudulentIP(event: MicrosoftEvent): boolean;
    protected abstract isForwardedOutsideOfOrg(event: MicrosoftEvent): boolean;
    protected abstract isMicrosoftActivity(event: MicrosoftEvent): boolean;
    // workload-identity predicates (KRAK-470): an application acting as itself, app-only, in an Entra ID record
    protected abstract isActorWorkloadIdentity(event: MicrosoftEvent): boolean;
    protected abstract isSelfAssignment(event: MicrosoftEvent): boolean;
    protected abstract isPrivilegedRole(event: MicrosoftEvent): boolean;
    protected abstract isMicrosoftFirstPartyApp(event: MicrosoftEvent): boolean;
    protected abstract isKnownVendorApp(event: MicrosoftEvent): boolean;
}

type FieldTypes = null | undefined | boolean | string | number;

type Fields = {
    [key: string]: FieldTypes;
} | null;

export interface MicrosoftEvent<T = any, X = any> {
    id: string;
    tenant: string;
    user_id: string | null; // actor
    object_id: string | null; // target
    user_type: UserType;
    source: 'MGMT' | 'UAL' | 'MAL';
    workload: Workload;
    record_type: AuditLogRecordType;
    operation: string;
    timestamp: string;
    processed_timestamp?: string; // timestamp when event was processed by ETL pipeline
    ip_address: string | null;
    /**
     * The acting application's id when the actor is a workload identity (an Entra ID record performed app-only by a
     * service principal, `UserId` = `ServicePrincipal_<objectId>`), from the record's `Actor` array; null for a
     * person, and absent on events stored before KRAK-470.
     */
    actor_app_id?: string | null;
    /** The acting service principal's object id, lowercased, when the actor is a workload identity; see {@link actor_app_id}. */
    actor_object_id?: string | null;
    reputation: Reputation | null;
    /**
     * The actor's address of an Entra ID directory record (`ActorIpAddress`; such records never carry `ClientIP`), kept
     * beside `ip_address` rather than in it so the legacy app grades the row as before KRAK-470; the v2 paths fold it
     * in (`classifier/actor-ip.ts` `withActorIp`). Null where absent, and absent on events stored before KRAK-470.
     */
    actor_ip_address?: string | null;
    /** The reputation the transform Lambda looked up for {@link actor_ip_address}; the same lifecycle as `reputation`. */
    actor_reputation?: Reputation | null;
    lockout_triggered?: boolean; // flag to prevent duplicate lockout attempts for the same event
    fields:
        | {
              [key in keyof T]: FieldTypes;
          }
        | null;
    description_fields?:
        | {
              [key in keyof T]: FieldTypes;
          }
        | null;
}

// see above Predicates class
export type PredicateKeys =
    | 'isAllowedCountry'
    | 'isAllowedRegion'
    | 'isAllowedASN'
    | 'isAllowedIP'
    | 'isUserMemberOfOrganisation'
    | 'isFraudulentIP'
    | 'isForwardedOutsideOfOrg'
    | 'isMicrosoftActivity'
    | 'isActorWorkloadIdentity'
    | 'isSelfAssignment'
    | 'isPrivilegedRole'
    | 'isMicrosoftFirstPartyApp'
    | 'isKnownVendorApp';

export interface Classification<T = any, X = any> {
    title: string;
    description: string | ((fields: Partial<{ [key in keyof X]: FieldTypes }>) => string);
    longDescription: string | ((fields: Partial<{ [key in keyof X]: FieldTypes }>) => string);
    severity: Severity;
    predicates?: Partial<{
        [key in PredicateKeys]: boolean;
    }>;
    fields?: Partial<{
        [key in keyof T]: Array<FieldTypes>;
    }>;
    description_fields?: Partial<{
        [key in keyof X]: FieldTypes;
    }>;
}

export type ClassificationWithoutTitle<T = any, X = any> = Omit<Classification<T, X>, 'title'>;

export interface EventMeta<T extends Fields = any, X extends Fields = any> {
    title: string;
    default: {
        longDescription: string;
        description: string;
        severity: Severity;
    };
    classifications?: ClassificationWithoutTitle[];
    fieldsExtractor?: (data: any) => T;
    descriptionExtractor?: (data: any) => X;
    /**
     * Defined only for a classifier given the tenant's application inventory (KRAK-470). Without it — the legacy app's
     * browser classifier, the summary run for an MSP on v1 — the operation is treated as undefined and graded the
     * `info` default, exactly as before KRAK-470, so a customer on the legacy app sees no new grading of any kind.
     */
    requiresWorkloadIdentities?: boolean;
}

/**
 * Coordinate
 *
 * `lat: number` - latitude
 *
 * `lng: number` - longtitude
 */
export interface Coord {
    lat: number;
    lng: number;
}

export interface EventRule {
    id: string;
    msp_id: string;
    name: string;
    created: string;
    start: string | null;
    end: string | null;
    source: 'msp' | 'tenant';
    scope: 'tenant' | 'group' | 'user';
    // targets
    target_tenants: string[];
    target_groups: string[];
    target_users: {
        tenant_id: string;
        user_id: string;
        upn: string;
    }[];
    // inputs
    country: string[];
    asn: number[];
    ip: string[];
    geo: {
        center: Coord;
        radius: number;
    }[];
    /**
     * Severity assigned AT INGEST to events this rule matches (monitoring event store spec §7.4, v_0053).
     *
     * `null`/absent is the legacy allow semantics every rule had before v_0053: the predicate returns
     * true, the classifier falls through, and the event lands on the fall-through branch (normally
     * `info`). Optional so the existing fixtures and the frontend's own object literals keep compiling.
     *
     * Never re-applied to an already-stored event (ruling MES-2 / R4).
     */
    target_severity?: Severity | null;
    /**
     * UPN of the operator who created the rule, server-resolved from the authenticated principal
     * (v_0053). `null` for rules that predate the column — no backfill invents an author.
     */
    created_by?: string | null;
}
